<template>
  <page-header title="Version Management" subtitle="Manage golden versions and track current versions across all projects">
    <button :disabled="loading" class="btn-outline-primary ml-auto h-10 mr-4" @click="refreshVersions">
      <ArrowPathIcon class="size-5 -ml-1 mr-2" />
      {{ loading ? 'Loading...' : 'Refresh' }}
    </button>
    <button class="btn-outline ml-auto h-10" @click="showMismatches">
      <ExclamationTriangleIcon class="size-5 -ml-1 mr-2" />
      Show Mismatches
    </button>
  </page-header>

  <div v-if="error" class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
    <div class="flex">
      <ExclamationTriangleIcon class="size-5 text-red-400 mr-3 mt-0.5" />
      <div>
        <h3 class="text-sm font-medium text-red-800">
          Error
        </h3>
        <div class="mt-1 text-sm text-red-700">
          {{ error }}
        </div>
      </div>
    </div>
  </div>

  <div v-if="mismatches.length > 0" class="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center">
        <ExclamationTriangleIcon class="size-5 text-yellow-400 mr-2" />
        <h3 class="text-lg font-semibold text-yellow-800">
          Version Mismatches ({{ mismatches.length }})
        </h3>
      </div>
      <button 
        class="text-yellow-700 hover:text-yellow-800 text-sm font-medium flex items-center"
        @click="mismatchesExpanded = !mismatchesExpanded"
      >
        {{ mismatchesExpanded ? 'Collapse' : 'Expand' }}
        <ChevronDownIcon 
          class="size-4 ml-1 transition-transform duration-200" 
          :class="{ 'rotate-180': mismatchesExpanded }"
        />
      </button>
    </div>
    <div v-if="mismatchesExpanded" class="space-y-2">
      <div
        v-for="mismatch in mismatches"
        :key="mismatch.projectName" 
        class="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 bg-white rounded border border-yellow-200 gap-2"
      >
        <span class="font-medium text-gray-900">{{ mismatch.projectName }}</span>
        <span class="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">
          Golden: {{ mismatch.goldenVersion }} | Current: {{ mismatch.currentVersion }}
        </span>
      </div>
    </div>
  </div>

  <div class="flex flex-col">
    <div class="-my-2">
      <div class="inline-block min-w-full py-2 align-middle">
        <div class="shadow-sm shadow-slate-200/20 border border-slate-300 rounded-md overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full min-w-full">
              <thead class="bg-slate-400/10">
                <tr>
                  <th class="sticky top-0 z-10 py-3 px-2 sm:px-3 lg:px-4 text-left text-sm font-semibold text-slate-500 h-10 leading-4">
                    Project
                  </th>
                  <th class="sticky top-0 z-10 py-3 px-2 sm:px-3 lg:px-4 text-left text-sm font-semibold text-slate-500 h-10 leading-4">
                    Golden Version
                  </th>
                  <th class="sticky top-0 z-10 py-3 px-2 sm:px-3 lg:px-4 text-left text-sm font-semibold text-slate-500 h-10 leading-4">
                    Current Version
                  </th>
                  <th class="sticky top-0 z-10 py-3 px-2 sm:px-3 lg:px-4 text-left text-sm font-semibold text-slate-500 h-10 leading-4">
                    Status
                  </th>
                  <th class="sticky top-0 z-10 py-3 px-2 sm:px-3 lg:px-4 text-left text-sm font-semibold text-slate-500 h-10 leading-4">
                    Last Deployed
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-400/30">
                <tr
                  v-for="(version, index) in versions"
                  :key="version.projectName" 
                  :class="index % 2 ? 'bg-zinc-300/10' : ''" 
                  class="hover:bg-slate-50"
                >
                  <td class="px-2 sm:px-3 lg:px-4 py-3 text-sm text-slate-500">
                    <div>
                      <div class="font-medium text-slate-900">
                        {{ version.projectName }}
                      </div>
                      <div v-if="version.notes" class="text-xs text-slate-400 mt-1">
                        {{ version.notes }}
                      </div>
                    </div>
                  </td>
                  <td class="px-2 sm:px-3 lg:px-4 py-3 text-sm text-slate-500">
                    <div class="flex items-center gap-2">
                      <input 
                        v-model="version.goldenVersion" 
                        class="w-24 px-2 py-1 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Version"
                        @input="onGoldenVersionChange(version)"
                      >
                      <button 
                        v-if="version.goldenVersionChanged"
                        class="btn-primary text-xs px-2 py-1"
                        @click="saveGoldenVersion(version)"
                      >
                        Save
                      </button>
                      <button 
                        v-if="version.goldenVersionChanged"
                        class="btn-outline text-xs px-2 py-1"
                        @click="discardGoldenVersion(version)"
                      >
                        Discard
                      </button>
                    </div>
                  </td>
                  <td class="px-2 sm:px-3 lg:px-4 py-3 text-sm text-slate-500">
                    <div class="flex items-center gap-2">
                      <input 
                        v-model="version.currentVersion" 
                        class="w-24 px-2 py-1 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Version"
                        @input="onCurrentVersionChange(version)"
                      >
                      <button 
                        v-if="version.currentVersionChanged"
                        class="btn-primary text-xs px-2 py-1"
                        @click="saveCurrentVersion(version)"
                      >
                        Save
                      </button>
                      <button 
                        v-if="version.currentVersionChanged"
                        class="btn-outline text-xs px-2 py-1"
                        @click="discardCurrentVersion(version)"
                      >
                        Discard
                      </button>
                    </div>
                  </td>
                  <td class="px-2 sm:px-3 lg:px-4 py-3 text-sm text-slate-500">
                    <span :class="getStatusClass(version)" class="inline-flex items-center px-2 py-1 rounded-full">
                      <CheckCircleIcon v-if="version.currentVersion === version.goldenVersion" class="size-4" />
                      <ExclamationTriangleIcon v-else class="size-4" />
                    </span>
                  </td>
                  <td class="px-2 sm:px-3 lg:px-4 py-3 text-sm text-slate-500">
                    {{ formatDate(version.lastDeployed) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useFetch } from '@vueuse/core';
import PageHeader from '../components/PageHeader.vue';
import { ArrowPathIcon, ExclamationTriangleIcon, CheckCircleIcon, ChevronDownIcon } from '@heroicons/vue/24/outline';
import { apiDomain } from '../composables/useConstants';
import { formatDate } from '../utils/formatters';

const toast = useToast();

const versions = ref([]);
const mismatches = ref([]);
const loading = ref(false);
const error = ref('');
const mismatchesExpanded = ref(false);

const refreshVersions = async () => {
  loading.value = true;
  error.value = '';
  
  try {
    const { data, error: fetchError } = await useFetch(`${apiDomain}/api/version`).get().json();
    
    if (fetchError.value) {
      throw new Error(fetchError.value.message || 'Failed to fetch versions');
    }
    
    // Add change tracking properties to each version
    versions.value = (data.value || []).map(version => ({
      ...version,
      originalGoldenVersion: version.goldenVersion,
      originalCurrentVersion: version.currentVersion,
      originalLastDeployed: version.lastDeployed,
      goldenVersionChanged: false,
      currentVersionChanged: false,
      lastDeployedChanged: false
    }));

    // Automatically load mismatches when versions are loaded
    await loadMismatches();
  } catch {
    error.value = 'Failed to load versions';
    toast.error('Failed to load versions');
  } finally {
    loading.value = false;
  }
};

const loadMismatches = async () => {
  try {
    const { data, error: fetchError } = await useFetch(`${apiDomain}/api/version/mismatches`).get().json();
    
    if (fetchError.value) {
      throw new Error(fetchError.value.message || 'Failed to fetch mismatches');
    }
    
    mismatches.value = data.value || [];
  } catch {
    console.error('Failed to load mismatches:', error);
  }
};

const showMismatches = async () => {
  try {
    await loadMismatches();
    
    if (mismatches.value.length === 0) {
      toast.success('No version mismatches found!');
    } else {
      toast.info(`Found ${mismatches.value.length} version mismatch(es)`);
    }
  } catch {
    toast.error('Failed to load mismatches');
  }
};

const onGoldenVersionChange = version => {
  version.goldenVersionChanged = version.goldenVersion !== version.originalGoldenVersion;
};

const onCurrentVersionChange = version => {
  version.currentVersionChanged = version.currentVersion !== version.originalCurrentVersion;
};

const saveCurrentVersion = async version => {
  try {
    await useFetch(`${apiDomain}/api/version/${version.projectName}/current`).post({ version: version.currentVersion }).json();
    version.originalCurrentVersion = version.currentVersion;
    version.currentVersionChanged = false;
    
    // Refresh the version data to get updated lastDeployed
    await refreshVersions();
    
    toast.success(`Current version updated for ${version.projectName}`);
  } catch {
    toast.error(`Failed to update current version for ${version.projectName}`);
  }
};

const saveGoldenVersion = async version => {
  try {
    await useFetch(`${apiDomain}/api/version/${version.projectName}/golden`).post({ version: version.goldenVersion }).json();
    version.originalGoldenVersion = version.goldenVersion;
    version.goldenVersionChanged = false;
    
    // Refresh the version data
    await refreshVersions();
    
    toast.success(`Golden version updated for ${version.projectName}`);
  } catch {
    toast.error(`Failed to update golden version for ${version.projectName}`);
  }
};

const discardGoldenVersion = version => {
  version.goldenVersion = version.originalGoldenVersion;
  version.goldenVersionChanged = false;
};

const discardCurrentVersion = version => {
  version.currentVersion = version.originalCurrentVersion;
  version.currentVersionChanged = false;
};

const getStatusClass = version => {
  if (version.currentVersion === version.goldenVersion) {
    return 'bg-green-100 text-green-800';
  }
  return 'bg-yellow-100 text-yellow-800';
};

onMounted(() => {
  refreshVersions();
});
</script>
